import ApiClient from '@afosto/api/ApiClient';

class Inventory {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Add Skus too location
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Add sku&#39;s that can be claimed on the location that is not tracking inventory
   * @returns {Promise}
   */
  addAvailableSkuToLocation(id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/locations/${id}/skus`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Add overage to the location
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Add overage that is available for location inventory
   * @returns {Promise}
   */
  addOverageToLocation(id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/locations/${id}/overage`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Delete Skus from location
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description remove sku&#39;s that can be claimed on the location that is not tracking inventory
   * @returns {Promise}
   */
  deleteAvailableSkuToLocation(id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/locations/${id}/skus`,
      method: 'delete',
      data: body,
      ...options,
    });
  }

  /**
   * List Skus for location
   *
   * @param {string} id
   * @param {Object} query - The optional path parameters
   * @param {string} [query.sku]
   * @param {Object} options - The additional request options
   * @description List sku&#39;s for location
   * @returns {Promise}
   */
  getAvailableSkuForLocation(id, query = {}, options = {}) {
    const params = {
      sku: query.sku,
    };

    return this.apiClient.request({
      url: `/wms/locations/${id}/skus`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get a warehouse item
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  getInventoryItem(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/inventory/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of warehouse items
   *
   * @param {Object} options - The additional request options
   * @description Get a list of warehouse items
   * @returns {Promise}
   */
  getInventoryItems(options = {}) {
    return this.apiClient.request({
      url: `/wms/inventory`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get positions
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Get positions for a warehouse
   * @returns {Promise}
   */
  listInventoryPositions(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/locations/${id}/positions`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get logs
   *
   * @param {string} id
   * @param {string} position
   * @param {Object} options - The additional request options
   * @description Get logs for a position in a warehouse
   * @returns {Promise}
   */
  listPositionLogs(id, position, options = {}) {
    return this.apiClient.request({
      url: `/wms/locations/${id}/positions/${position}/logs`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Search items inventory
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Get a list of inventory items
   * @returns {Promise}
   */
  searchInventory(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/search/items`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Search a inventory summary
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Get a list of inventory summaries
   * @returns {Promise}
   */
  searchInventorySummary(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/inventory`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Set the absolute stock at a warehouse
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Get a list of inventory summaries
   * @returns {Promise}
   */
  setInventory(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/inventory`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Update a warehouse item
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Update the warehouse item
   * @returns {Promise}
   */
  updateItem(id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/inventory/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Verify position
   *
   * @param {string} id
   * @param {string} position
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Verify position count in a warehouse
   * @returns {Promise}
   */
  verifyPosition(id, position, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/locations/${id}/positions/${position}/verify`,
      method: 'post',
      data: body,
      ...options,
    });
  }
}

export const inventory = new Inventory();

export default Inventory;
